import axios from 'axios';

// Order a session's resources by the order their card slugs appear in `cardOrder` — the drag order
// arranged in the CMS session-filter entry (surfaced per session in scheduleMapping[id].cardOrder).
// Resources whose slug is not listed keep their existing relative order (stable sort).
function sortResourcesByCardOrder(resources, cardOrder) {
  if (!Array.isArray(cardOrder) || cardOrder.length === 0) return resources;
  const rank = new Map(cardOrder.map((slug, index) => [slug, index]));
  return [...resources].sort((a, b) => {
    const ia = rank.has(a.slug) ? rank.get(a.slug) : Infinity;
    const ib = rank.has(b.slug) ? rank.get(b.slug) : Infinity;
    return ia - ib;
  });
}

const state = () => ({
  isLoading: false,
  allResources: [],
  allQuotes: [],
  allBooks: [],
  allWatchLists: [],
  flattenedAllResources: [],
  flattenedQuotes: [],
  flattenedBooks: [],
  flattenedWatchLists: [],
  sessions: [],
  topics: [],
  filteredSessions: [],
  filteredTopics: [],
  filteredItems: [], // For GIC filter buttons
  selectedFilter: {}, // For GIC filter buttons
  clickedFilter: {}, // Currently clicked filter button
  filteredResources: {}, // Filtered resources for GIC
  showFilterModal: false,
  showFilterResults: false,
  schedule: [],
  scheduleMapping: {}, // <session cms id>: schedule obj
  sessionIdScheduleMapping: {}, // <session id> : schedule obj
  filterTypeScheduleMapping: {}, // <gicConferenceId>: [schedule objects] for GIC filtering
  searchSuggestions: [],
  filterType: '',
  allBookMarkList: [], // uses by display with resources
  bookMarkListFetchedAt: 0, // when allBookMarkList was last fetched (ms epoch)
  bookMarkListFetchKey: '', // '<conferenceType>:<locale>' the cached list belongs to
  bookMarkMap: {},
  videoProgress: {}, // videoId: progress
});

const mutations = {
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setAllResources(state, allResources) {
    state.allResources = allResources;
  },
  setAllQuotes(state, allQuotes) {
    state.allQuotes = allQuotes;
  },
  setAllBooks(state, allBooks) {
    state.allBooks = allBooks;
  },
  setAllBookMarkList(state, allBookMarkList) {
    state.allBookMarkList = allBookMarkList;
  },
  setBookMarkListFetchMeta(state, { key, at }) {
    state.bookMarkListFetchKey = key;
    state.bookMarkListFetchedAt = at;
  },
  setWatchLists(state, allWatchLists) {
    state.allWatchLists = allWatchLists;
  },
  setFlattenedAllResources(state, flattenedAllResources) {
    state.flattenedAllResources = flattenedAllResources;
  },
  setFlattenedQuotes(state, flattenedQuotes) {
    state.flattenedQuotes = flattenedQuotes;
  },
  setFlattenedBooks(state, flattenedBooks) {
    state.flattenedBooks = flattenedBooks;
  },
  setFlattenedWatchLists(state, flattenedWatchLists) {
    state.flattenedWatchLists = flattenedWatchLists;
  },
  setSessions(state, sessions) {
    state.sessions = sessions;
  },
  setTopics(state, topics) {
    state.topics = topics;
  },
  setSchedule(state, schedule) {
    state.schedule = schedule;
    const scheduleMapping = {};
    const sessionIdScheduleMapping = {};
    const filterTypeScheduleMapping = {};
    for (const scheduleObj of schedule) {
      scheduleMapping[scheduleObj.id] = scheduleObj;
      if (scheduleObj.sessionId) {
        sessionIdScheduleMapping[scheduleObj.sessionId] = scheduleObj;
      }
      // Group by gicConferenceId for GIC filtering
      if (scheduleObj.gicConferenceId) {
        if (!(scheduleObj.gicConferenceId in filterTypeScheduleMapping)) {
          filterTypeScheduleMapping[scheduleObj.gicConferenceId] = [];
        }
        filterTypeScheduleMapping[scheduleObj.gicConferenceId].push(scheduleObj);
      }
    }
    state.scheduleMapping = scheduleMapping;
    state.sessionIdScheduleMapping = sessionIdScheduleMapping;
    state.filterTypeScheduleMapping = filterTypeScheduleMapping;
  },
  setSearchSuggestions(state, searchSuggestions) {
    state.searchSuggestions = searchSuggestions;
  },
  setShowFilterModal(state, { showFilterModal, filterType }) {
    state.showFilterModal = showFilterModal;
    state.filterType = filterType;
  },
  setFilteredValues(state, { filteredSessions, filteredTopics }) {
    state.filteredSessions = filteredSessions;
    state.filteredTopics = filteredTopics;
  },
  setShowFilterResults(state, showFilterResults) {
    state.showFilterResults = showFilterResults;
  },
  setFilteredItems(state, { filteredItems }) {
    state.filteredItems = filteredItems;
  },
  setSelectedFilter(state, selectedFilter) {
    state.selectedFilter = selectedFilter;
  },
  setClickedFilter(state, clickedFilter) {
    state.clickedFilter = clickedFilter;
  },
  clearFilteredResources(state) {
    state.filteredResources = {};
  },
  setFilteredResources(state) {
    if (state.selectedFilter.type === 'Sessions') {
      const filteredSessionIds = state.filteredItems;
      // filteredResource object format
      // "<session id>:[resources]"
      const filteredResources = {};
      for (const resource of state.flattenedAllResources) {
        for (const filteredSessionId of filteredSessionIds) {
          if (!resource.sessionIds || !resource.sessionIds.includes(filteredSessionId)) {
            continue;
          }

          const readableSessionId = state.scheduleMapping[filteredSessionId].sessionId;
          const readableSessionName = state.scheduleMapping[filteredSessionId].title;

          if (!(readableSessionId in filteredResources)) {
            filteredResources[readableSessionId] = {
              sessionId: readableSessionId,
              name: readableSessionName,
              resources: [],
              cardOrder: state.scheduleMapping[filteredSessionId].cardOrder || [],
            };
          }

          filteredResources[readableSessionId].resources.push(resource);

          const existSlug = new Set();
          filteredResources[readableSessionId].resources = filteredResources[
            readableSessionId
          ].resources.filter(item => {
            if (existSlug.has(item.slug)) return false;
            existSlug.add(item.slug);
            return true;
          });
        }
      }
      // Order each session's resources by the CMS session-filter card order.
      for (const key in filteredResources) {
        filteredResources[key].resources = sortResourcesByCardOrder(
          filteredResources[key].resources,
          filteredResources[key].cardOrder,
        );
      }
      state.filteredResources = filteredResources;

      const filteredBooks = state.flattenedBooks.filter(resource => {
        const isSessionsMatch =
          filteredSessionIds.length > 0
            ? filteredSessionIds.some(id => resource.sessionId === id)
            : true;
        return isSessionsMatch;
      });
      state.filteredBooks = filteredBooks;
    }
  },
  setFilteredMaterials(state) {
    if (state.filterType === 'Sessions') {
      const filteredSessionIds = state.filteredSessions;

      console.log('in set filterdmaterial');
      // filteredResource object format
      // "<session id>:[resources]"
      const filteredResources = {};
      for (const resource of state.flattenedAllResources) {
        for (const filteredSessionId of filteredSessionIds) {
          if (!resource.sessionIds || !resource.sessionIds.includes(filteredSessionId)) {
            continue;
          }

          const readableSessionId = state.scheduleMapping[filteredSessionId].sessionId;
          const readableSessionName = state.scheduleMapping[filteredSessionId].title;

          if (!(readableSessionId in filteredResources)) {
            filteredResources[readableSessionId] = {
              sessionId: readableSessionId,
              name: readableSessionName,
              resources: [],
              cardOrder: state.scheduleMapping[filteredSessionId].cardOrder || [],
            };
          }

          filteredResources[readableSessionId].resources.push(resource);

          const existSlug = new Set();
          filteredResources[readableSessionId].resources = filteredResources[
            readableSessionId
          ].resources.filter(item => {
            if (existSlug.has(item.slug)) return false;
            existSlug.add(item.slug);
            return true;
          });
        }
      }
      // Order each session's resources by the CMS session-filter card order.
      for (const key in filteredResources) {
        filteredResources[key].resources = sortResourcesByCardOrder(
          filteredResources[key].resources,
          filteredResources[key].cardOrder,
        );
      }
      state.filteredResources = filteredResources;

      const filteredQuotes = state.flattenedQuotes.filter(resource => {
        const isSessionsMatch =
          filteredSessionIds.length > 0
            ? filteredSessionIds.some(id => resource.sessionIds && resource.sessionIds.includes(id))
            : true;
        return isSessionsMatch;
      });
      state.filteredQuotes = filteredQuotes;

      const filteredBooks = state.flattenedBooks.filter(resource => {
        const isSessionsMatch =
          filteredSessionIds.length > 0
            ? filteredSessionIds.some(id => resource.sessionIds && resource.sessionIds.includes(id))
            : true;
        return isSessionsMatch;
      });
      state.filteredBooks = filteredBooks;
    } else if (state.filterType === 'Topics') {
      const filteredTopics = state.filteredTopics;
      const filteredResources = state.flattenedAllResources.filter(resource => {
        const isTopicsMatch =
          filteredTopics.length > 0
            ? filteredTopics.some(topic => {
                if (Object.prototype.hasOwnProperty.call(resource, 'topics')) {
                  for (const key in resource.topics) {
                    if (resource.topics[key] === topic) {
                      return true;
                    }
                  }
                }
              })
            : true;
        return isTopicsMatch;
      });
      state.filteredResources = filteredResources;

      const filteredQuotes = state.flattenedQuotes.filter(resource => {
        const isTopicsMatch =
          filteredTopics.length > 0
            ? filteredTopics.some(topic => {
                if (Object.prototype.hasOwnProperty.call(resource, 'topics')) {
                  for (const key in resource.topics) {
                    if (resource.topics[key] === topic) {
                      return true;
                    }
                  }
                }
              })
            : true;
        return isTopicsMatch;
      });
      state.filteredQuotes = filteredQuotes;

      const filteredBooks = state.flattenedBooks.filter(resource => {
        const isTopicsMatch =
          filteredTopics.length > 0
            ? filteredTopics.some(topic => {
                if (Object.prototype.hasOwnProperty.call(resource, 'topics')) {
                  for (const key in resource.topics) {
                    if (resource.topics[key] === topic) {
                      return true;
                    }
                  }
                }
              })
            : true;
        return isTopicsMatch;
      });
      state.filteredBooks = filteredBooks;
    }
    // state.showFilterModal = true;
    // state.showFilterResults = true;
  },
  clearState() {
    state.allResources = [];
    state.allQuotes = [];
    state.allBooks = [];
    state.flattenedAllResources = [];
    state.flattenedQuotes = [];
    state.flattenedBooks = [];
    state.sessions = [];
    state.topics = [];
    state.filteredSessions = [];
    state.filteredTopics = [];
    state.filteredItems = [];
    state.selectedFilter = {};
    state.showFilterModal = false;
    state.showFilterResults = false;
    state.schedule = [];
    state.searchSuggestions = [];
    state.filterType = '';
    state.allWatchLists = [];
    state.flattenedWatchLists = [];
  },
  toggleBookMarkList(state, { slug, flag }) {
    if (flag) {
      let item = null;
      const itemList = state.allResources
        .map(section => section.resources.filter(r => r.slug === slug))
        .flat();
      if (itemList.length > 0) {
        item = itemList[0];
      }

      // if all resource can't find, find from watchlist
      if (!item) {
        const itemListWatchList = state.allWatchLists
          .map(section => section.resources.filter(r => r.slug === slug))
          .flat();
        if (itemListWatchList.length > 0) {
          item = itemListWatchList[0];
        }
      }

      if (item) {
        // First-ever bookmark: create the My List section locally so the
        // optimistic add stays visible even if the follow-up refetch fails.
        if (!state.allBookMarkList[0]) {
          state.allBookMarkList.push({ resources: [] });
        } else if (!Array.isArray(state.allBookMarkList[0].resources)) {
          state.allBookMarkList[0].resources = [];
        }
        state.allBookMarkList[0].resources.unshift(item);
      }
    } else {
      state.allBookMarkList.forEach(section => {
        section.resources = section.resources.filter(r => r.slug !== slug);
      });
    }
  },
  setFilterType(state, filterType) {
    state.filterType = filterType;
  },
  setBookMarkMapItem(state, { slug, existSlug }) {
    const newBookMarkMap = state.bookMarkMap;
    if (!existSlug) {
      delete newBookMarkMap[slug];
    } else {
      newBookMarkMap[slug] = existSlug;
    }
    state.bookMarkMap = newBookMarkMap;
  },
  setVideoProgressItem(state, { slug, videoTime }) {
    state.videoProgress[slug] = videoTime;
  },
};

// Helper function to get search keywords endpoint based on conference type
const getSearchKeywordsEndpoint = conferenceType => {
  return conferenceType === 'GIC' ? '/cms/gic/search/keywords' : '/cms/search/keywords';
};

const actions = {
  async fetchAllResources(
    { dispatch, commit, rootState },
    { locale = 'en', conferenceType = 'Global' },
  ) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data: allresources } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/resources/all',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            locale: locale,
            conferenceType: conferenceType,
          },
        },
      );

      console.log('all resources', allresources);
      if (conferenceType === 'Global') {
        const { data: allMarks } = await axios.get(process.env.VUE_APP_API_URL + '/videoprogress', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            locale: locale,
            conferenceType: conferenceType,
          },
        });
        if (typeof allMarks === 'string') {
          console.log('error', allMarks);
        } else {
          try {
            for (const bookmark of allMarks.data) {
              commit('setBookMarkMapItem', {
                slug: bookmark.videoId,
                existSlug: true,
              });
            }
          } catch (e) {
            console.error(e);
          }
        }
      }

      // const allQuotes = allresources.filter(
      //   (resource) => resource.resources.at(0).type === "sm_quotes"
      // );

      const allQuotes = [];
      const allBooks = [];
      const allResources = [];
      for (const resource of allresources) {
        const firstResource = resource.resources.at(0);

        if (firstResource) {
          switch (firstResource.type) {
            case 'sm_quotes':
              allQuotes.push(resource);
              break;
            case 'sm_books':
              allBooks.push(resource);
              break;
            default:
              allResources.push(resource);
          }
        } else {
          allResources.push(resource);
        }
      }
      commit('setAllResources', allResources);
      commit('setAllQuotes', allQuotes);
      commit('setAllBooks', allBooks);

      const resourceList = [];
      for (const material of allResources) {
        for (const resource of material.resources) {
          Object.defineProperty(resource, 'name', { value: material.name });
          resourceList.push(resource);
        }
      }
      commit('setFlattenedAllResources', resourceList);

      const quoteList = [];
      for (const material of allQuotes) {
        for (const resource of material.resources) {
          quoteList.push(resource);
        }
      }

      const bookList = [];
      for (const material of allBooks) {
        for (const resource of material.resources) {
          bookList.push(resource);
        }
      }
      commit('setFlattenedQuotes', quoteList);
      commit('setFlattenedBooks', bookList);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch session materials.', {
        root: true,
      });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async fetchWatchLists(
    { dispatch, commit, rootState },
    { locale = 'en', conferenceType = 'Global' },
  ) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data: watchLists } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/resources/watch-lists',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            locale: locale,
            conferenceType: conferenceType,
          },
        },
      );
      commit('setWatchLists', watchLists);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch watch lists.', {
        root: true,
      });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async fetchVideoProgress({ dispatch, commit, rootState }, { slug }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data: videoProgress } = await axios.get(
        process.env.VUE_APP_API_URL + '/videoprogress/' + slug,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {},
        },
      );
      if (!videoProgress) return;
      commit('setVideoProgressItem', {
        slug: videoProgress.videoId,
        videoTime: videoProgress.videoTime,
      });
      // TODO to split videoprogress with book mark
      commit('setBookMarkMapItem', {
        slug: videoProgress.videoId,
        existSlug: videoProgress.isBookmarked,
      });
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch watch lists.', {
        root: true,
      });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async fetchBookMarkLists(
    { dispatch, commit, state, rootState },
    { locale = 'en', conferenceType = 'Global', force = false },
  ) {
    // Serve the in-store list for up to a day. Bookmark toggles pass force:true
    // so the list re-syncs immediately; a different conference/locale key always
    // refetches so one conference's list is never shown for another.
    const cacheKey = `${conferenceType}:${locale}`;
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    if (
      !force &&
      state.allBookMarkList.length &&
      state.bookMarkListFetchKey === cacheKey &&
      Date.now() - state.bookMarkListFetchedAt < ONE_DAY_MS
    ) {
      return;
    }
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const endpoint =
        conferenceType === 'GIC' ? '/cms/gic/resources/my-lists' : '/cms/resources/my-lists';
      const { data: bookMarkList } = await axios.get(process.env.VUE_APP_API_URL + endpoint, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          locale: locale,
          conferenceType: conferenceType,
        },
      });
      commit('setAllBookMarkList', bookMarkList);
      commit('setBookMarkListFetchMeta', { key: cacheKey, at: Date.now() });
      if (
        bookMarkList &&
        bookMarkList.length > 0 &&
        bookMarkList[0].resources &&
        bookMarkList[0].resources.length > 0
      ) {
        for (const resource of bookMarkList[0].resources) {
          commit('setBookMarkMapItem', {
            slug: resource.slug,
            existSlug: true,
          });
        }
      }
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch watch lists.', {
        root: true,
      });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async fetchTopics({ dispatch, commit, rootState }, { locale = 'en', conferenceType = 'Global' }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data: topics } = await axios.get(process.env.VUE_APP_API_URL + '/cms/filter/topics', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          locale: locale,
          conferenceType: conferenceType,
        },
      });
      topics.sort((a, b) => a.order - b.order);
      commit('setTopics', topics);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch topics.', {
        root: true,
      });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async fetchSchedule(
    { commit, dispatch, rootState },
    { locale = 'en', conferenceType = 'Global' },
  ) {
    try {
      const accessToken = rootState.user.token;
      const endpoint =
        conferenceType === 'GIC' ? '/cms/gic/schedules/session/all' : '/cms/schedules/session/all';
      const fullUrl = process.env.VUE_APP_API_URL + endpoint;
      console.log('Fetching schedule from:', fullUrl, { locale, conferenceType });
      const { status, data } = await axios.get(fullUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          locale: locale,
          conferenceType: conferenceType,
        },
      });
      console.log('Schedule response:', {
        status,
        dataLength: Array.isArray(data) ? data.length : 'not an array',
        data,
      });
      if (status !== 200) throw new Error('Failed in getting schedule');
      if (!data || (Array.isArray(data) && data.length === 0)) {
        console.warn('Schedule data is empty or not an array');
      }
      commit('setSchedule', data || []);
    } catch (e) {
      console.error('Error fetching schedule:', e);
      console.error('Error details:', {
        message: e.message,
        response: e.response?.data,
        status: e.response?.status,
      });
      dispatch('toast/error', 'Failed to fetch schedule.', {
        root: true,
      });
      throw e;
    }
  },
  async fetchSearchSuggestions(
    { commit, dispatch, rootState },
    { locale = 'en', conferenceType = 'Global' },
  ) {
    try {
      const accessToken = rootState.user.token;
      const endpoint = getSearchKeywordsEndpoint(conferenceType);
      const { status, data } = await axios.get(process.env.VUE_APP_API_URL + endpoint, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          locale: locale,
          conferenceType: conferenceType,
        },
      });
      if (status !== 200) throw new Error('Failed in getting search keywords');
      commit('setSearchSuggestions', data);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch schedule.', {
        root: true,
      });
    }
  },

  updateShowFilterModal({ commit }, { showFilterModal, filterType }) {
    commit('setShowFilterModal', { showFilterModal, filterType });
  },

  updateFilteredValues({ commit }, { filteredSessions, filteredTopics }) {
    commit('setFilteredValues', { filteredSessions, filteredTopics });
  },
  updateFilteredMaterials({ commit }) {
    commit('setFilteredMaterials');
  },

  updateShowFilterResults({ commit }, showFilterResults) {
    commit('setShowFilterResults', showFilterResults);
  },
  updateFilteredItems({ commit }, { filteredItems }) {
    commit('setFilteredItems', { filteredItems });
  },
  setSelectedFilter({ commit }, selectedFilter) {
    commit('setSelectedFilter', selectedFilter);
  },
  setClickedFilter({ commit }, clickedFilter) {
    commit('setClickedFilter', clickedFilter);
  },
  updateFilteredResources({ commit }) {
    commit('setFilteredResources');
  },

  clearState({ commit }) {
    commit('clearState');
  },
  toggleBookMark({ commit, state }, { slug }) {
    const existFlag = slug in state.bookMarkMap;
    commit('setBookMarkMapItem', { slug, existSlug: !existFlag });
  },
  toggleBookMarkList({ commit }, { type, slug, flag }) {
    commit('toggleBookMarkList', { type, slug, flag });
  },
  setFilterType({ commit }, filterType) {
    commit('setFilterType', filterType);
  },
};

const getters = {
  myListHeader(state) {
    return state.allBookMarkList !== 'undefined' &&
      state.allBookMarkList &&
      state.allBookMarkList.length > 0
      ? state.allBookMarkList[0].name
      : '';
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
